import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
} from "./ui/sidebar";
import { Button } from "./ui/button";
import {
  LayoutDashboard,
  Users,
  Settings,
  Package,
  Menu,
  Bell,
  Search,
  User,
  LogOut,
  BookOpen,
  FileText,
} from "lucide-react";
import authUtils from "../utils/auth";

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const navigationItems = [
    {
      name: "대시보드",
      icon: LayoutDashboard,
      href: "/dashboard",
      active: location.pathname === "/dashboard" || location.pathname === "/",
    },
    {
      name: "상품 등록",
      icon: Package,
      href: "/product-register",
      active: location.pathname === "/product-register",
    },
    {
      name: "상품 관리",
      icon: BookOpen,
      href: "/course-management",
      active: location.pathname === "/course-management",
    },
    {
      name: "학생부 파일",
      icon: FileText,
      href: "/student-records",
      active: location.pathname === "/student-records",
    },
    { name: "사용자 관리", icon: Users, href: "/users", active: false },
    { name: "설정", icon: Settings, href: "/settings", active: false },
  ];

  const handleNavigation = (href: string) => {
    navigate(href);
  };

  const handleLogout = () => {
    authUtils.clearTokens();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* 사이드바 */}
      <Sidebar className={sidebarOpen ? "w-64" : "w-16"}>
        <SidebarHeader>
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <h1 className="text-xl font-bold text-primary">S-Class Admin</h1>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </SidebarHeader>

        <SidebarContent>
          <nav className="space-y-2">
            {navigationItems.map((item) => (
              <Button
                key={item.name}
                variant={item.active ? "secondary" : "ghost"}
                className={`w-full justify-start ${
                  sidebarOpen ? "px-3" : "px-2"
                }`}
                onClick={() => handleNavigation(item.href)}
              >
                <item.icon className="h-5 w-5" />
                {sidebarOpen && <span className="ml-3">{item.name}</span>}
              </Button>
            ))}
          </nav>
        </SidebarContent>

        <SidebarFooter>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                <User className="h-4 w-4 text-primary-foreground" />
              </div>
              {sidebarOpen && (
                <div>
                  <p className="text-sm font-medium">관리자</p>
                  <p className="text-xs text-muted-foreground">
                    admin@s-class.com
                  </p>
                </div>
              )}
            </div>
            {sidebarOpen && (
              <Button
                variant="ghost"
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                로그아웃
              </Button>
            )}
          </div>
        </SidebarFooter>
      </Sidebar>

      {/* 메인 콘텐츠 영역 */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 상단 헤더 */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-2xl font-semibold text-gray-900">대시보드</h2>
            </div>

            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon">
                <Search className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5" />
              </Button>
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                <User className="h-4 w-4 text-primary-foreground" />
              </div>
            </div>
          </div>
        </header>

        {/* 메인 콘텐츠 */}
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
};
